import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/services/api";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  GraduationCap,
  BarChart3,
  Users,
  Award,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Filter,
  FolderCog,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function GestionCompetences() {
  const { currentUser } = useApp();
  const [competences, setCompetences] = useState([]);
  const [grille, setGrille] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategorie, setSelectedCategorie] = useState("all");
  const [categories, setCategories] = useState([]);
  const [categoriesConfig, setCategoriesConfig] = useState([]);
  const [activeTab, setActiveTab] = useState("competences");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCompetence, setSelectedCompetence] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    categorie: "",
    ordre: 0,
  });
  const [saving, setSaving] = useState(false);

  // Category management states
  const [showAddCatDialog, setShowAddCatDialog] = useState(false);
  const [showEditCatDialog, setShowEditCatDialog] = useState(false);
  const [showDeleteCatDialog, setShowDeleteCatDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catFormData, setCatFormData] = useState({
    code: "",
    libelle: "",
    description: "",
    ordre: 0,
  });

  // Export states
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [exportFilters, setExportFilters] = useState({
    poste: "all",
    secteur: "all",
  });
  const [exporting, setExporting] = useState(false);

  // Check permissions
  const canManage = ["Admin", "RH", "Manager"].includes(currentUser?.role);
  const canDelete = currentUser?.role === "Admin";

  useEffect(() => {
    loadData();
    loadCollaborateurs();
    loadSecteurs();
  }, []);

  const loadCollaborateurs = async () => {
    try {
      const response = await api.get('/collaborateurs?actif=true');
      const data = Array.isArray(response) ? response : (response.collaborateurs || []);
      setCollaborateurs(data);
    } catch (error) {
      console.error('Error loading collaborateurs:', error);
    }
  };

  const loadSecteurs = async () => {
    try {
      const response = await api.get('/secteurs');
      const data = Array.isArray(response) ? response : (response.secteurs || []);
      setSecteurs(data);
    } catch (error) {
      console.error('Error loading secteurs:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [competencesRes, grilleRes, categoriesRes, categoriesConfigRes, statsRes] = await Promise.all([
        api.get("/api/competences"),
        api.get("/api/competences/grille"),
        api.get("/api/competences/categories"),
        api.get("/api/categories-competences?actif=true").catch(() => []),
        api.get("/api/competences/statistiques").catch(() => null),
      ]);
      
      setCompetences(competencesRes || []);
      setGrille(grilleRes || {});
      setCategories(categoriesRes || []);
      setCategoriesConfig(categoriesConfigRes || []);
      if (statsRes) setStats(statsRes);
    } catch (error) {
      console.error("Error loading competences:", error);
      toast.error("Erreur lors du chargement des compétences");
    } finally {
      setLoading(false);
    }
  };

  // Get all categories - only from configured categories
  const allCategories = useMemo(() => {
    const configuredCodes = categoriesConfig.map((c) => c.code);
    const fromCompetences = categories.filter(Boolean);
    const allCodes = [...new Set([...configuredCodes, ...fromCompetences])];
    return allCodes.sort();
  }, [categories, categoriesConfig]);

  // Helper function to get category label
  const getCategoryLabel = (code) => {
    const configCat = categoriesConfig.find(c => c.code === code);
    if (configCat) return configCat.libelle || code;
    return code;
  };

  // Get unique postes from collaborateurs
  const uniquePostes = useMemo(() => {
    const postes = collaborateurs
      .map(c => c.poste)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
    return postes.sort();
  }, [collaborateurs]);

  // Filter collaborateurs for export
  const filteredCollaborateursForExport = useMemo(() => {
    return collaborateurs.filter(c => {
      const matchesPoste = exportFilters.poste === "all" || c.poste === exportFilters.poste;
      const matchesSecteur = exportFilters.secteur === "all" || c.secteur_id === exportFilters.secteur;
      return matchesPoste && matchesSecteur;
    });
  }, [collaborateurs, exportFilters]);

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      // Get evaluations for all collaborateurs
      const evaluationsResponse = await api.get('/evaluations-competences');
      const evaluations = evaluationsResponse?.evaluations || [];
      
      // Get filtered collaborateurs
      const collabsToExport = filteredCollaborateursForExport;
      
      if (collabsToExport.length === 0) {
        toast.error('Aucun collaborateur à exporter avec les filtres sélectionnés');
        setExporting(false);
        return;
      }

      if (competences.length === 0) {
        toast.error('Aucune compétence définie');
        setExporting(false);
        return;
      }

      // Create the matrix
      // Header row: "Collaborateur", "Poste", "Département", then all competences
      const headers = ['Collaborateur', 'Poste', 'Département', ...competences.map(c => c.nom)];
      
      // Data rows: one per collaborateur
      const dataRows = collabsToExport.map(collab => {
        const secteur = secteurs.find(s => s.id === collab.secteur_id);
        const row = [
          `${collab.prenom} ${collab.nom}`,
          collab.poste || '-',
          secteur?.libelle || '-'
        ];
        
        // Add evaluation for each competence
        competences.forEach(comp => {
          const evaluation = evaluations.find(
            e => e.collaborateur_id === collab.id && e.competence_id === comp.id
          );
          row.push(evaluation?.niveau || 0);
        });
        
        return row;
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Collaborateur
        { wch: 20 }, // Poste
        { wch: 20 }, // Département
        ...competences.map(() => ({ wch: 15 })) // Competences
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Matrice Compétences');

      // Add legend sheet
      const legendData = [
        ['Légende des notations'],
        [''],
        ['Niveau', 'Description'],
        [0, 'Non évalué'],
        [1, 'Débutant'],
        [2, 'Intermédiaire'],
        [3, 'Confirmé'],
        [4, 'Expert']
      ];
      const wsLegend = XLSX.utils.aoa_to_sheet(legendData);
      wsLegend['!cols'] = [{ wch: 10 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, wsLegend, 'Légende');

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `matrice_competences_${date}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
      
      toast.success(`Export réussi: ${collabsToExport.length} collaborateur(s), ${competences.length} compétence(s)`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const filteredCompetences = useMemo(() => {
    return competences.filter((c) => {
      const matchesSearch =
        c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategorie =
        selectedCategorie === "all" || c.categorie === selectedCategorie;
      return matchesSearch && matchesCategorie && c.actif !== false;
    });
  }, [competences, searchTerm, selectedCategorie]);

  const handleAdd = () => {
    setFormData({ nom: "", description: "", categorie: "", ordre: 0 });
    setShowAddDialog(true);
  };

  const handleEdit = (competence) => {
    setSelectedCompetence(competence);
    setFormData({
      nom: competence.nom || "",
      description: competence.description || "",
      categorie: competence.categorie || "",
      ordre: competence.ordre || 0,
    });
    setShowEditDialog(true);
  };

  const handleDelete = (competence) => {
    setSelectedCompetence(competence);
    setShowDeleteDialog(true);
  };

  const handleSaveNew = async () => {
    if (!formData.nom.trim()) {
      toast.error("Le nom de la compétence est requis");
      return;
    }
    
    try {
      setSaving(true);
      await api.post("/api/competences", formData);
      toast.success("Compétence créée avec succès");
      setShowAddDialog(false);
      loadData();
    } catch (error) {
      console.error("Error creating competence:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!formData.nom.trim()) {
      toast.error("Le nom de la compétence est requis");
      return;
    }
    
    try {
      setSaving(true);
      await api.put(`/api/competences/${selectedCompetence.id}`, formData);
      toast.success("Compétence modifiée avec succès");
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      console.error("Error updating competence:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSaving(true);
      await api.delete(`/api/competences/${selectedCompetence.id}`);
      toast.success("Compétence supprimée");
      setShowDeleteDialog(false);
      loadData();
    } catch (error) {
      console.error("Error deleting competence:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setSaving(false);
    }
  };

  const moveCompetence = async (competence, direction) => {
    const newOrdre = (competence.ordre || 0) + (direction === "up" ? -1 : 1);
    try {
      await api.put(`/api/competences/${competence.id}`, { ordre: newOrdre });
      loadData();
    } catch (error) {
      console.error("Error moving competence:", error);
    }
  };

  // ================== Category Management ==================
  const handleAddCategory = () => {
    setCatFormData({ code: "", libelle: "", description: "", ordre: categoriesConfig.length });
    setShowAddCatDialog(true);
  };

  const handleEditCategory = (cat) => {
    setSelectedCategory(cat);
    setCatFormData({
      code: cat.code || "",
      libelle: cat.libelle || "",
      description: cat.description || "",
      ordre: cat.ordre || 0,
    });
    setShowEditCatDialog(true);
  };

  const handleDeleteCategory = (cat) => {
    setSelectedCategory(cat);
    setShowDeleteCatDialog(true);
  };

  const handleSaveNewCategory = async () => {
    if (!catFormData.code.trim() || !catFormData.libelle.trim()) {
      toast.error("Le code et le libellé sont requis");
      return;
    }
    try {
      setSaving(true);
      await api.post("/api/categories-competences", catFormData);
      toast.success("Catégorie créée avec succès");
      setShowAddCatDialog(false);
      loadData();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEditCategory = async () => {
    if (!catFormData.libelle.trim()) {
      toast.error("Le libellé est requis");
      return;
    }
    try {
      setSaving(true);
      await api.put(`/api/categories-competences/${selectedCategory.id}`, {
        libelle: catFormData.libelle,
        description: catFormData.description,
        ordre: catFormData.ordre,
      });
      toast.success("Catégorie modifiée avec succès");
      setShowEditCatDialog(false);
      loadData();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteCategory = async () => {
    try {
      setSaving(true);
      await api.delete(`/api/categories-competences/${selectedCategory.id}`);
      toast.success("Catégorie supprimée");
      setShowDeleteCatDialog(false);
      loadData();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-competences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="h-7 w-7 text-primary" />
            Gestion des Compétences
          </h1>
          <p className="text-gray-600 mt-1">
            Paramétrez et gérez les compétences des collaborateurs
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowExportDialog(true)} 
            variant="outline"
            data-testid="export-competences-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter en Excel
          </Button>
          {canManage && (
            <Button onClick={handleAdd} data-testid="add-competence-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle Compétence
            </Button>
          )}
        </div>
      </div>

      {/* Grille de notation */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Award className="h-5 w-5" />
            Grille de Notation
          </CardTitle>
          <CardDescription>
            Échelle d'évaluation des compétences de 1 à 4
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(grille).map(([niveau, info]) => (
              <div
                key={niveau}
                className="p-4 rounded-lg border-2 transition-all hover:shadow-md"
                style={{ borderColor: info.couleur + "40", backgroundColor: info.couleur + "10" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: info.couleur }}
                  >
                    {niveau}
                  </div>
                  <span className="font-semibold" style={{ color: info.couleur }}>
                    {info.label}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{info.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Compétences</p>
                  <p className="text-2xl font-bold">{stats.nb_competences}</p>
                </div>
                <GraduationCap className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Collaborateurs</p>
                  <p className="text-2xl font-bold">{stats.nb_collaborateurs}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Taux Évaluation Moyen</p>
                  <p className="text-2xl font-bold">
                    {stats.statistiques?.length > 0
                      ? Math.round(
                          stats.statistiques.reduce((a, b) => a + b.taux_evaluation, 0) /
                            stats.statistiques.length
                        )
                      : 0}%
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Note Moyenne Globale</p>
                  <p className="text-2xl font-bold">
                    {stats.statistiques?.length > 0
                      ? (
                          stats.statistiques
                            .filter((s) => s.moyenne > 0)
                            .reduce((a, b) => a + b.moyenne, 0) /
                          stats.statistiques.filter((s) => s.moyenne > 0).length
                        ).toFixed(1)
                      : "-"}
                    /4
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-amber-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for List, Stats and Categories */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="competences">Liste des Compétences</TabsTrigger>
          <TabsTrigger value="statistiques">Statistiques Détaillées</TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <FolderCog className="h-4 w-4" />
            Catégories ({categoriesConfig.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="competences">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle>Compétences ({filteredCompetences.length})</CardTitle>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      data-testid="search-competence-input"
                    />
                  </div>
                  <Select value={selectedCategorie} onValueChange={setSelectedCategorie}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="filter-categorie">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {allCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {getCategoryLabel(cat)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredCompetences.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune compétence trouvée</p>
                  {canManage && (
                    <Button variant="outline" className="mt-4" onClick={handleAdd}>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une compétence
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Ordre</TableHead>
                        <TableHead>Nom</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Catégorie</TableHead>
                        {stats && <TableHead className="text-center">Évalués</TableHead>}
                        {stats && <TableHead className="text-center">Moyenne</TableHead>}
                        {canManage && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCompetences
                        .sort((a, b) => (a.ordre || 0) - (b.ordre || 0))
                        .map((competence) => {
                          const statComp = stats?.statistiques?.find(
                            (s) => s.competence_id === competence.id
                          );
                          return (
                            <TableRow key={competence.id} data-testid={`competence-row-${competence.id}`}>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">{competence.ordre || 0}</span>
                                  {canManage && (
                                    <div className="flex flex-col">
                                      <button
                                        onClick={() => moveCompetence(competence, "up")}
                                        className="p-0.5 hover:bg-gray-100 rounded"
                                      >
                                        <ChevronUp className="h-3 w-3" />
                                      </button>
                                      <button
                                        onClick={() => moveCompetence(competence, "down")}
                                        className="p-0.5 hover:bg-gray-100 rounded"
                                      >
                                        <ChevronDown className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{competence.nom}</TableCell>
                              <TableCell className="text-gray-600 max-w-xs truncate">
                                {competence.description || "-"}
                              </TableCell>
                              <TableCell>
                                {competence.categorie ? (
                                  <Badge variant="outline">{getCategoryLabel(competence.categorie)}</Badge>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </TableCell>
                              {stats && (
                                <TableCell className="text-center">
                                  <span className="text-sm">
                                    {statComp?.nb_evalues || 0}/{statComp?.nb_total || 0}
                                  </span>
                                </TableCell>
                              )}
                              {stats && (
                                <TableCell className="text-center">
                                  {statComp?.moyenne > 0 ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <div className="flex items-center justify-center gap-1">
                                            <div
                                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                              style={{
                                                backgroundColor:
                                                  grille[Math.round(statComp.moyenne)]?.couleur ||
                                                  "#gray",
                                              }}
                                            >
                                              {statComp.moyenne.toFixed(1)}
                                            </div>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>
                                            {grille[Math.round(statComp.moyenne)]?.label || "N/A"}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </TableCell>
                              )}
                              {canManage && (
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(competence)}
                                      data-testid={`edit-competence-${competence.id}`}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    {canDelete && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(competence)}
                                        className="text-red-500 hover:text-red-700"
                                        data-testid={`delete-competence-${competence.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistiques">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Statistiques par Compétence
              </CardTitle>
              <CardDescription>
                Distribution des niveaux pour chaque compétence
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!stats || stats.statistiques?.length === 0 ? (
                <div className="text-center py-12">
                  <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune statistique disponible</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {stats.statistiques.map((stat) => (
                    <div
                      key={stat.competence_id}
                      className="p-4 border rounded-lg hover:shadow-sm transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                        <div>
                          <h4 className="font-semibold">{stat.competence_nom}</h4>
                          {stat.categorie && (
                            <Badge variant="outline" className="mt-1">
                              {stat.categorie}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-500">
                            {stat.nb_evalues}/{stat.nb_total} évalués ({stat.taux_evaluation}%)
                          </span>
                          {stat.moyenne > 0 && (
                            <div
                              className="px-3 py-1 rounded-full text-white font-semibold"
                              style={{
                                backgroundColor: grille[Math.round(stat.moyenne)]?.couleur,
                              }}
                            >
                              {stat.moyenne.toFixed(1)}/4
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Distribution bars */}
                      <div className="flex gap-2 items-end h-16">
                        {Object.entries(stat.distribution).map(([niveau, count]) => {
                          const maxCount = Math.max(...Object.values(stat.distribution), 1);
                          const height = (count / maxCount) * 100;
                          return (
                            <TooltipProvider key={niveau}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex-1 flex flex-col items-center">
                                    <div
                                      className="w-full rounded-t transition-all cursor-pointer hover:opacity-80"
                                      style={{
                                        height: `${Math.max(height, 4)}%`,
                                        backgroundColor: grille[niveau]?.couleur || "#ccc",
                                        minHeight: "4px",
                                      }}
                                    />
                                    <span className="text-xs mt-1 text-gray-500">{count}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    Niveau {niveau}: {grille[niveau]?.label}
                                  </p>
                                  <p className="text-xs text-gray-400">{count} collaborateur(s)</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>Débutant</span>
                        <span>Expert</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FolderCog className="h-5 w-5" />
                  Catégories de Compétences
                </CardTitle>
                <CardDescription>
                  Gérez les catégories utilisées pour classer les compétences
                </CardDescription>
              </div>
              {canManage && (
                <Button onClick={handleAddCategory} data-testid="add-category-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle Catégorie
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {categoriesConfig.length === 0 ? (
                <div className="text-center py-12">
                  <FolderCog className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune catégorie configurée</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Les catégories par défaut seront utilisées.
                  </p>
                  {canManage && (
                    <Button variant="outline" className="mt-4" onClick={handleAddCategory}>
                      <Plus className="h-4 w-4 mr-2" />
                      Créer une catégorie
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Libellé</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Compétences</TableHead>
                        {canManage && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoriesConfig.map((cat) => {
                        const compCount = competences.filter(c => c.categorie === cat.code).length;
                        return (
                          <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">
                                {cat.code}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{cat.libelle}</TableCell>
                            <TableCell className="text-gray-500 text-sm max-w-xs truncate">
                              {cat.description || "-"}
                            </TableCell>
                            <TableCell>
                              <Badge variant={compCount > 0 ? "secondary" : "outline"}>
                                {compCount} compétence{compCount !== 1 ? "s" : ""}
                              </Badge>
                            </TableCell>
                            {canManage && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditCategory(cat)}
                                    data-testid={`edit-category-${cat.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  {canDelete && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteCategory(cat)}
                                      className="text-red-500 hover:text-red-700"
                                      disabled={compCount > 0}
                                      title={compCount > 0 ? "Supprimez d'abord les compétences de cette catégorie" : "Supprimer"}
                                      data-testid={`delete-category-${cat.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Compétence</DialogTitle>
            <DialogDescription>
              Définissez une nouvelle compétence à évaluer pour les collaborateurs
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom de la compétence *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: Utilisation de l'agrafeuse"
                data-testid="competence-nom-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la compétence..."
                rows={3}
                data-testid="competence-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="categorie">Catégorie</Label>
              <Select
                value={formData.categorie}
                onValueChange={(value) => setFormData({ ...formData, categorie: value })}
              >
                <SelectTrigger data-testid="competence-categorie-select">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ordre">Ordre d'affichage</Label>
              <Input
                id="ordre"
                type="number"
                value={formData.ordre}
                onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value) || 0 })}
                min={0}
                data-testid="competence-ordre-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveNew} disabled={saving} data-testid="save-new-competence-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la Compétence</DialogTitle>
            <DialogDescription>Modifiez les informations de la compétence</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nom">Nom de la compétence *</Label>
              <Input
                id="edit-nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                data-testid="edit-competence-nom-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="edit-competence-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-categorie">Catégorie</Label>
              <Select
                value={formData.categorie}
                onValueChange={(value) => setFormData({ ...formData, categorie: value })}
              >
                <SelectTrigger data-testid="edit-competence-categorie-select">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-ordre">Ordre d'affichage</Label>
              <Input
                id="edit-ordre"
                type="number"
                value={formData.ordre}
                onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value) || 0 })}
                min={0}
                data-testid="edit-competence-ordre-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-competence-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Supprimer la Compétence
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la compétence "{selectedCompetence?.nom}" ?
              Cette action désactivera la compétence mais conservera l'historique des évaluations.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={saving}
              data-testid="confirm-delete-competence-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCatDialog} onOpenChange={setShowAddCatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle Catégorie de Compétence</DialogTitle>
            <DialogDescription>
              Créez une catégorie pour organiser vos compétences
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cat-code">Code *</Label>
              <Input
                id="cat-code"
                value={catFormData.code}
                onChange={(e) => setCatFormData({ ...catFormData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: TECH, SECU, QUAL"
                maxLength={20}
                data-testid="category-code-input"
              />
              <p className="text-xs text-gray-500">Code court unique pour identifier la catégorie</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-libelle">Libellé *</Label>
              <Input
                id="cat-libelle"
                value={catFormData.libelle}
                onChange={(e) => setCatFormData({ ...catFormData, libelle: e.target.value })}
                placeholder="Ex: Technique, Sécurité"
                data-testid="category-libelle-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-description">Description</Label>
              <Textarea
                id="cat-description"
                value={catFormData.description}
                onChange={(e) => setCatFormData({ ...catFormData, description: e.target.value })}
                placeholder="Description optionnelle..."
                rows={2}
                data-testid="category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCatDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveNewCategory} disabled={saving} data-testid="save-new-category-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditCatDialog} onOpenChange={setShowEditCatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier la Catégorie</DialogTitle>
            <DialogDescription>
              Modifiez les informations de la catégorie &quot;{selectedCategory?.code}&quot;
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input value={catFormData.code} disabled className="bg-gray-50" />
              <p className="text-xs text-gray-500">Le code ne peut pas être modifié</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cat-libelle">Libellé *</Label>
              <Input
                id="edit-cat-libelle"
                value={catFormData.libelle}
                onChange={(e) => setCatFormData({ ...catFormData, libelle: e.target.value })}
                data-testid="edit-category-libelle-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cat-description">Description</Label>
              <Textarea
                id="edit-cat-description"
                value={catFormData.description}
                onChange={(e) => setCatFormData({ ...catFormData, description: e.target.value })}
                rows={2}
                data-testid="edit-category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCatDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEditCategory} disabled={saving} data-testid="save-edit-category-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={showDeleteCatDialog} onOpenChange={setShowDeleteCatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Supprimer la Catégorie
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la catégorie &quot;{selectedCategory?.libelle}&quot; ({selectedCategory?.code}) ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteCatDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteCategory}
              disabled={saving}
              data-testid="confirm-delete-category-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Exporter la Matrice des Compétences
            </DialogTitle>
            <DialogDescription>
              Exportez les évaluations de compétences de tous les collaborateurs en format Excel.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Filter by Poste */}
            <div className="space-y-2">
              <Label>Filtrer par poste</Label>
              <Select 
                value={exportFilters.poste} 
                onValueChange={(value) => setExportFilters({...exportFilters, poste: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les postes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les postes</SelectItem>
                  {uniquePostes.map((poste) => (
                    <SelectItem key={poste} value={poste}>{poste}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filter by Département/Secteur */}
            <div className="space-y-2">
              <Label>Filtrer par département</Label>
              <Select 
                value={exportFilters.secteur} 
                onValueChange={(value) => setExportFilters({...exportFilters, secteur: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les départements" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les départements</SelectItem>
                  {secteurs.map((secteur) => (
                    <SelectItem key={secteur.id} value={secteur.id}>
                      {secteur.libelle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview count */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>{filteredCollaborateursForExport.length}</strong> collaborateur(s) seront exportés
                avec <strong>{competences.length}</strong> compétence(s).
              </p>
            </div>

            {/* Legend reminder */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 font-medium mb-2">Format de l'export :</p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Colonnes : Compétences</li>
                <li>• Lignes : Collaborateurs</li>
                <li>• Intersections : Notation (0 = Non évalué, 4 = Expert)</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleExportExcel} 
              disabled={exporting || filteredCollaborateursForExport.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Export en cours...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger Excel
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
